import type { TFunction } from 'i18next';

import type { ImportSkipReason } from '../../../common/hooks/use-network-api.hook';
import type { NetworkLinkMethod } from '../../../common/models/network.model';

/**
 * Words for the network's closed vocabularies (insight-contract: materials,
 * appearances, colour families, finishes).
 *
 * The catalogue facets already translate most of them under
 * PAGES.MY_PRODUCTS.APPEARANCE; the few they never needed (terracotta,
 * zellige, cement tile…) live in this page's own namespace. A value neither
 * knows is shown as words rather than as a key.
 */
export function vocabLabel(t: TFunction, value?: string | null): string {
    if (!value) return '';
    const key = value.toUpperCase().replace(/-/g, '_');
    const humanised = value.replace(/-/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
    return t(`NETWORK_RETAILER.VOCAB.${key}`, { defaultValue: t(`PAGES.MY_PRODUCTS.APPEARANCE.${key}`, { defaultValue: humanised }) }) as string;
}

/** 60 × 120 cm */
export function formatLabel(format?: { width?: number; height?: number; unit?: string } | null): string {
    if (!format?.width || !format?.height) return '';
    return `${format.width} × ${format.height} ${format.unit || 'cm'}`;
}

/** The country's name in the reader's language, from its ISO code. */
export function countryLabel(code: string | undefined, language: string): string {
    if (!code) return '';
    try {
        const names = new (Intl as any).DisplayNames([language || 'en'], { type: 'region' });
        return names.of(code.toUpperCase()) || code;
    } catch {
        return code;
    }
}

export function methodLabel(t: TFunction, method: NetworkLinkMethod): string {
    return t(`NETWORK_RETAILER.METHOD.${method.toUpperCase()}`) as string;
}

export function skipReasonLabel(t: TFunction, reason: ImportSkipReason): string {
    return t(`NETWORK_RETAILER.IMPORT.REASON.${reason.toUpperCase()}`) as string;
}
